// The HTML layer over the game canvas: join screen, chat window, status banner, debug stats.
// Inventory, action bars, unit frames and raid frames will live here too, as page elements, not canvas drawing.
import { factionById } from '../../../shared/factions.js';
import { parseChat, type Channel } from '../../../shared/chat.js';
import { parseGroupCommand, type GroupCommand } from '../../../shared/party.js';
import type { ServerMsg } from '../../../shared/protocol.js';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
export const store = { get(k: string) { try { return localStorage.getItem(k); } catch { return null; } }, set(k: string, v: string) { try { localStorage.setItem(k, v); } catch { /* private mode */ } } };

// ---------- chat ----------
const LABEL: Record<Channel, string> = { say: 'Say', zone: 'Zone', faction: 'Faction', whisper: 'Whisper', party: 'Party', alliance: 'Alliance', guild: 'Guild', system: '' };

export class ChatBox {
  private log = $('chatLog'); private form = $<HTMLFormElement>('chatForm'); private input = $<HTMLInputElement>('chatIn'); private ch = $('chatCh');
  sticky: Channel = 'say';
  onSend: (ch: Channel, text: string, to?: string) => void = () => {};
  onCommand: (c: GroupCommand) => void = () => {};
  myName = '';
  /** Channels the player chose to hide (Settings → Chat). */
  hidden = new Set<Channel>();
  onLine: (ch: Channel, fromMe: boolean) => void = () => {};

  constructor() {
    this.setupTabs();
    this.form.addEventListener('submit', e => {
      e.preventDefault();
      const g = parseGroupCommand(this.input.value);
      if (g === 'usage') { this.system('Try /invite name, /pcmd add|kick|leader name, /pcmd leave, /acmd add name, /acmd leave, /seek note, /seek off, /follow name, /follow off, /check name, /mute name, /block name, /report name.'); this.close(); return; }
      if (g) { this.onCommand(g); this.close(); return; }
      const p = parseChat(this.input.value);
      if (p && p.text) { if (p.ch !== 'whisper') this.sticky = p.ch; this.onSend(p.ch, p.text, p.to); }
      this.close();
    });
    this.input.addEventListener('keydown', e => { if (e.key === 'Escape') { e.preventDefault(); this.close(); } });
    this.input.addEventListener('input', () => { const m = /^\/(\w+)\s/.exec(this.input.value); if (m) { const p = parseChat(this.input.value + 'x'); if (p) this.ch.textContent = LABEL[p.ch]; } });
  }
  get open(): boolean { return !this.form.hidden; }
  openBox(slash: boolean, prefill?: string): void {
    this.form.hidden = false; this.ch.textContent = LABEL[this.sticky]; this.wake();
    this.input.value = prefill ?? (slash ? '/' : (this.sticky === 'say' ? '' : '/' + this.sticky + ' '));
    this.input.focus();
  }
  close(): void { this.form.hidden = true; this.input.value = ''; this.input.blur(); }

  /** Tabs: All, Local (say, zone, faction), Party (party, alliance), Whispers. Others light up when something new arrives. */
  private tab = 'all'; private idleT = 0;
  private static TABS: Record<string, Channel[]> = { local: ['say', 'zone', 'faction'], party: ['party', 'alliance'], whisper: ['whisper'] };
  private setupTabs(): void {
    for (const b of document.querySelectorAll<HTMLButtonElement>('[data-chtab]')) b.addEventListener('click', () => {
      this.tab = b.dataset.chtab!; this.log.dataset.tab = this.tab;
      for (const o of document.querySelectorAll<HTMLButtonElement>('[data-chtab]')) o.classList.toggle('on', o === b);
      b.classList.remove('unread'); this.wake();
    });
    this.wake();
  }
  /** Something happened: full strength for a while, then it fades back so the world shows through. */
  private wake(): void { $('chat').classList.remove('idle'); clearTimeout(this.idleT); this.idleT = window.setTimeout(() => { if (!this.open) $('chat').classList.add('idle'); }, 20000); }

  /** Click a name: open their card. Muted people (this device) aren't shown. */
  onName: (name: string, x: number, y: number) => void = () => {};
  isMuted: (name: string) => boolean = () => false;

  line(ch: Channel, name: string | null, text: string, color?: string, to?: string): void {
    if (this.hidden.has(ch)) return;
    if (name && name !== this.myName && ch !== 'system' && this.isMuted(name)) return;
    this.wake();
    if (ch !== 'system') for (const [t, chans] of Object.entries(ChatBox.TABS)) if (t !== this.tab && chans.includes(ch)) document.querySelector(`[data-chtab="${t}"]`)?.classList.add('unread');
    this.onLine(ch, name === this.myName);
    const d = document.createElement('div'); d.className = 'line ' + ch;
    const prefix = ch === 'whisper' ? (to && name === this.myName ? `To ${to}` : `From ${name}`) : ch === 'system' ? '' : `[${LABEL[ch]}] ${name}`;
    if (prefix) {
      const n = document.createElement('span'); n.className = 'nm'; n.textContent = prefix + ': '; if (color && ch !== 'whisper') n.style.color = color;
      const who = ch === 'whisper' && to && name === this.myName ? to : name;
      if (who && who !== this.myName) { n.classList.add('who'); n.title = `${who}: open their card`; n.addEventListener('click', e => this.onName(who, e.clientX, e.clientY)); }
      d.appendChild(n);
    }
    d.appendChild(document.createTextNode(text));
    this.log.appendChild(d);
    while (this.log.children.length > 60) this.log.firstChild!.remove();
  }
  fromServer(m: Extract<ServerMsg, { type: 'chat' }>): void { this.line(m.ch, m.name, m.text, factionById(m.faction)?.color, m.to); }
  system(text: string): void { this.line('system', null, text); }
}

export function setStatus(text: string): void { const s = $('status'); s.textContent = text; s.hidden = !text; }

export class DebugPanel {
  private el = $('debug'); visible: boolean;
  constructor() {
    const q = new URLSearchParams(location.search);
    // on by default on computers, off on touch screens where space is tight; ?debug=1 or ?debug=0 overrides
    this.visible = q.get('debug') ? q.get('debug') !== '0' : !matchMedia('(pointer:coarse)').matches;
    this.el.hidden = !this.visible;
    addEventListener('keydown', e => { if (e.code === 'F3') { e.preventDefault(); this.onToggle(!this.visible); } });
  }
  /** F3 pressed: the settings decide and call show(). */
  onToggle: (on: boolean) => void = on => this.show(on);
  show(on: boolean): void { this.visible = on; this.el.hidden = !on; }
  set(lines: string[] | null): void { this.el.hidden = !this.visible || !lines; if (this.visible && lines) this.el.textContent = lines.join('\n'); }
}

/** Short messages in the middle of the screen: loot, level ups, refusals. */
export function toast(text: string, kind: 'loot' | 'info' | 'level' | 'err' = 'info'): void {
  const box = $('toasts'), t = document.createElement('div');
  t.className = 'toast ' + kind; t.textContent = text; box.appendChild(t);
  while (box.children.length > 4) box.firstChild!.remove();
  setTimeout(() => t.remove(), kind === 'level' ? 3500 : 2200);
}
